import { useEffect, useState } from 'react';
import { api } from '../../api/client.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { Loader, Alert, Modal, Toast, Empty, relativeTime } from '../../components/ui.jsx';
import CubMascot, { AVATAR_OPTIONS } from '../../components/CubMascot.jsx';

/* Passphrase generator: three easy words, a symbol and two digits. Long
   enough to be safe, simple enough for a 7-year-old to type, and it meets
   every password rule (upper, lower, digit AND special character) so the
   generated password never bounces off the server's policy. */
const WORDS = [
  'Panda', 'Rocket', 'Mango', 'Turtle', 'Comet', 'Waffle', 'Tiger', 'Pebble',
  'Cactus', 'Dolphin', 'Muffin', 'Falcon', 'Pickle', 'Zebra', 'Marble', 'Jungle',
];
const SYMBOLS = '!?#@';
const makePassword = () => {
  const pick = () => WORDS[Math.floor(Math.random() * WORDS.length)];
  const sym = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
  return `${pick()}${pick()}${pick()}${sym}${Math.floor(10 + Math.random() * 90)}`;
};

const ROLE_PILL = {
  student: { cls: 't-sky', icon: '🎒', label: 'Student' },
  admin: { cls: 't-mint', icon: '👩‍🏫', label: 'Admin' },
  superadmin: { cls: 't-grape', icon: '🛡️', label: 'Superadmin' },
};

const blankUser = () => ({
  userId: '',
  fullName: '',
  role: 'student',
  schoolId: '',
  grade: '',
  age: '',
  parentMobile: '',
  avatar: 'cub-teal',
  password: makePassword(),
  mustChangePassword: true,
});

/* Hand a new student's login to their parent over WhatsApp with one tap.
   This is a free "click to send" wa.me link - no API and no cost: it opens
   WhatsApp with the message pre-filled and the superadmin presses send. The
   parent number is stored as 10 national digits, so prefix the India
   country code and strip anything that is not a digit. */
const parentWaMessage = ({ fullName, userId, password }) =>
  `Hi! 🐻 Your child ${fullName}'s CyberCub account is ready.\n\n` +
  `Sign in at https://cybercub.org\n` +
  `User ID: ${userId}\n` +
  `Password: ${password}\n\n` +
  `They will be asked to set their own password the first time they sign in. Please keep this message safe.`;

const parentWaLink = (mobile, text) => {
  const digits = String(mobile || '').replace(/\D/g, '');
  const intl = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
};

export default function SuperUsers() {
  const { user: me } = useAuth();

  const [users, setUsers] = useState(null);
  const [schools, setSchools] = useState([]);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const [role, setRole] = useState('all');
  const [schoolId, setSchoolId] = useState('all');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(blankUser);
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ schoolId: '', grade: '', defaultPassword: makePassword(), text: '' });
  const [bulkResult, setBulkResult] = useState(null);

  const [resetFor, setResetFor] = useState(null);
  const [resetPwd, setResetPwd] = useState('');

  // Self-registered students arrive with no school, so they are invisible
  // to every school admin until one is assigned here.
  const [assignFor, setAssignFor] = useState(null);
  const [assignSchool, setAssignSchool] = useState('');

  const load = () =>
    api
      .superUsers({ role, schoolId, search, status })
      .then(({ users: list }) => setUsers(list))
      .catch((err) => setError(err.message));

  useEffect(() => {
    api.superSchools().then(({ schools: list }) => setSchools(list)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, schoolId, search, status]);

  /* ---------------- create one ---------------- */
  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const openCreate = () => {
    setForm({ ...blankUser(), schoolId: schools[0]?.id ?? '' });
    setFormError('');
    setCreated(null);
    setCreateOpen(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError('');
    try {
      const payload = {
        userId: form.userId.trim(),
        fullName: form.fullName.trim(),
        role: form.role,
        password: form.password,
        avatar: form.avatar,
        mustChangePassword: form.mustChangePassword,
        schoolId: form.role === 'superadmin' ? null : Number(form.schoolId) || null,
        grade: form.role === 'student' ? form.grade : '',
        age: form.role === 'student' && form.age ? Number(form.age) : null,
        parentMobile: form.role === 'student' ? form.parentMobile.trim() : '',
      };
      await api.createUser(payload);
      setCreated({
        userId: payload.userId,
        password: form.password,
        fullName: payload.fullName,
        parentMobile: payload.parentMobile,
      });
      await load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setBusy(false);
    }
  };

  /* ---------------- bulk create ---------------- */
  const parseBulk = (text) =>
    text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [userId, ...rest] = line.split(',').map((p) => p.trim());
        return { userId, fullName: rest.join(', ') || userId };
      });

  const submitBulk = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError('');
    try {
      const result = await api.bulkCreateUsers({
        schoolId: Number(bulk.schoolId),
        grade: bulk.grade,
        defaultPassword: bulk.defaultPassword,
        students: parseBulk(bulk.text),
      });
      setBulkResult(result);
      await load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setBusy(false);
    }
  };

  /* ---------------- row actions ---------------- */
  const toggleActive = async (u) => {
    try {
      await api.updateUser(u.role, u.id, { isActive: !u.isActive });
      setToast(`${u.fullName} is now ${u.isActive ? 'paused' : 'active'}`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.resetUserPassword(resetFor.role, resetFor.id, { newPassword: resetPwd, mustChangePassword: true });
      setToast(`New password set for ${resetFor.userId}`);
      setResetFor(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const doAssign = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.updateUser(assignFor.role, assignFor.id, { schoolId: Number(assignSchool) });
      setToast(`${assignFor.fullName} added to their school`);
      setAssignFor(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const unlock = async (u) => {
    try {
      await api.unlockUser(u.role, u.id);
      setToast(`${u.fullName} can sign in again`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const removeUser = async (u) => {
    // Deleting cascades to progress, quiz attempts and badges, so make
    // sure the superadmin really means it.
    const ok = window.confirm(
      `Delete ${u.fullName} (${u.userId})?\n\nThis also removes all of their progress, quiz attempts and badges. This cannot be undone.`,
    );
    if (!ok) return;
    try {
      await api.deleteUser(u.role, u.id);
      setToast(`Deleted ${u.userId}`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>👥 Users</h1>
          <p>
            Register students, admins and other superadmins. This is how new accounts are made —
            there is no public sign-up. Create them here, or let a school admin add their own
            students; any account with <strong>no school</strong> can be assigned one here.
          </p>
        </div>
        <div className="row gap-sm wrap">
          <button type="button" className="btn btn-ghost" onClick={() => { setBulkOpen(true); setBulkResult(null); setFormError(''); setBulk((b) => ({ ...b, schoolId: schools[0]?.id ?? '' })); }}>
            📋 Bulk add class
          </button>
          <button type="button" className="btn" onClick={openCreate}>➕ Register a user</button>
        </div>
      </div>

      {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

      <div className="filter-bar">
        <input
          type="search"
          placeholder="🔍 Search name, User ID or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search users"
        />
        <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Filter by role">
          <option value="all">All roles</option>
          <option value="student">Students</option>
          <option value="admin">Admins</option>
          <option value="superadmin">Superadmins</option>
        </select>
        <select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} aria-label="Filter by school">
          <option value="all">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          <option value="all">All accounts</option>
          <option value="active">Active only</option>
          <option value="paused">Paused only</option>
          <option value="locked">Locked only</option>
        </select>
      </div>

      {!users ? (
        <Loader label="Loading users…" />
      ) : users.length === 0 ? (
        <Empty icon="👥" title="No users match" text="Try a different filter, or register a new user." />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>School</th>
                <th>Grade</th>
                <th>Last sign-in</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const pill = ROLE_PILL[u.role];
                return (
                  <tr key={`${u.role}-${u.id}`}>
                    <td>
                      <div className="cell-user">
                        <CubMascot avatar={u.avatar} size={36} />
                        <div>
                          <strong>{u.fullName}</strong>
                          <span className="faint small">{u.userId}</span>
                          {u.parentMobile && (
                            <span className="faint small">📱 {u.parentMobile}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`pill pill-tone ${pill.cls}`}>
                        {pill.icon} {pill.label}
                      </span>
                    </td>
                    <td className="small">
                      {u.schoolName ?? (
                        <span className="row gap-xs wrap">
                          <span className="pill pill-warn">No school</span>
                          {u.role !== 'superadmin' && (
                            <button
                              type="button"
                              className="btn btn-sm btn-ghost"
                              onClick={() => { setAssignFor(u); setAssignSchool(schools[0]?.id ?? ''); }}
                            >
                              Assign
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="small">{u.grade ?? '—'}</td>
                    <td className="small muted">{relativeTime(u.lastLoginAt)}</td>
                    <td>
                      {u.isLocked ? (
                        <span className="pill pill-bad">🔒 Locked</span>
                      ) : (
                        <span className={`pill ${u.isActive ? 'pill-good' : 'pill-bad'}`}>
                          {u.isActive ? 'Active' : 'Paused'}
                        </span>
                      )}
                      {u.mustChangePassword && (
                        <span className="pill pill-warn" style={{ marginLeft: 4 }}>New password due</span>
                      )}
                    </td>
                    <td>
                      <div className="row gap-xs wrap">
                        {u.isLocked && (
                          <button type="button" className="btn btn-sm" onClick={() => unlock(u)}>
                            🔓 Unlock
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          onClick={() => { setResetFor(u); setResetPwd(makePassword()); }}
                        >
                          Reset password
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          onClick={() => toggleActive(u)}
                          disabled={u.role === 'superadmin' && u.id === me.id}
                        >
                          {u.isActive ? 'Pause' : 'Activate'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost danger"
                          onClick={() => removeUser(u)}
                          disabled={u.role === 'superadmin' && u.id === me.id}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------------- create modal ---------------- */}
      <Modal open={createOpen} title="Register a new user" onClose={() => setCreateOpen(false)} wide>
        {created ? (
          <div className="created-box">
            <h3>✅ {created.fullName} is ready to sign in!</h3>
            <p className="muted">
              Write these down and hand them over. The password is shown once — after this you can
              only reset it, not read it back.
            </p>
            <div className="credential">
              <div><span className="faint small">User ID</span><strong>{created.userId}</strong></div>
              <div><span className="faint small">Password</span><strong>{created.password}</strong></div>
            </div>
            <div className="row gap-sm wrap mt-md">
              {created.parentMobile && (
                <a
                  className="btn"
                  href={parentWaLink(created.parentMobile, parentWaMessage(created))}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📲 Send WhatsApp to parent
                </a>
              )}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigator.clipboard?.writeText(`User ID: ${created.userId}\nPassword: ${created.password}`)}
              >
                📋 Copy
              </button>
              <button type="button" className="btn btn-ghost" onClick={openCreate}>➕ Register another</button>
              <button type="button" className="btn btn-quiet" onClick={() => setCreateOpen(false)}>Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submitCreate}>
            {formError && <Alert kind="bad" onDismiss={() => setFormError('')}>{formError}</Alert>}

            <div className="field">
              <label>Account type</label>
              <div className="role-picker">
                {Object.entries(ROLE_PILL).map(([key, r]) => (
                  <button
                    key={key}
                    type="button"
                    className={`role-option ${r.cls} ${form.role === key ? 'is-picked' : ''}`}
                    onClick={() => setForm((f) => ({ ...f, role: key }))}
                    aria-pressed={form.role === key}
                  >
                    <span aria-hidden="true">{r.icon}</span>
                    <strong>{r.label}</strong>
                    <span className="small">
                      {key === 'student' && 'Learns in the portal'}
                      {key === 'admin' && 'Views one school'}
                      {key === 'superadmin' && 'Controls everything'}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-row">
              <div className="field">
                <label htmlFor="u-name">Full name</label>
                <input id="u-name" value={form.fullName} onChange={set('fullName')} required maxLength={120} />
              </div>
              <div className="field">
                <label htmlFor="u-id">User ID (used to log in)</label>
                <input
                  id="u-id"
                  value={form.userId}
                  onChange={set('userId')}
                  required
                  minLength={3}
                  maxLength={50}
                  placeholder="e.g. aarav.g"
                  autoCapitalize="none"
                  spellCheck="false"
                />
                <div className="hint">Letters, numbers, dots, dashes and underscores.</div>
              </div>
            </div>

            {form.role !== 'superadmin' && (
              <div className="form-row">
                <div className="field">
                  <label htmlFor="u-school">School</label>
                  <select id="u-school" value={form.schoolId} onChange={set('schoolId')} required>
                    <option value="">— choose a school —</option>
                    {schools.map((s) => (
                      <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                    ))}
                  </select>
                </div>
                {form.role === 'student' && (
                  <div className="field">
                    <label htmlFor="u-grade">Grade / class</label>
                    <input id="u-grade" value={form.grade} onChange={set('grade')} placeholder="e.g. Grade 4" maxLength={20} />
                  </div>
                )}
              </div>
            )}

            {form.role === 'student' && (
              <>
                <div className="form-row">
                  <div className="field" style={{ maxWidth: 180 }}>
                    <label htmlFor="u-age">Age</label>
                    <input id="u-age" type="number" min={5} max={51} value={form.age} onChange={set('age')} />
                  </div>
                  <div className="field">
                    <label htmlFor="u-wa">Parent / guardian WhatsApp number</label>
                    <input
                      id="u-wa"
                      type="tel"
                      value={form.parentMobile}
                      onChange={set('parentMobile')}
                      required
                      placeholder="e.g. 98765 43210"
                      inputMode="numeric"
                      maxLength={15}
                    />
                    <div className="hint">We use this to reach a parent about the account.</div>
                  </div>
                </div>
                <div className="field">
                  <label>Starting cub</label>
                  <div className="avatar-picker">
                    {AVATAR_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        className={`avatar-option ${form.avatar === opt ? 'is-picked' : ''}`}
                        onClick={() => setForm((f) => ({ ...f, avatar: opt }))}
                        aria-label={`Choose ${opt}`}
                        aria-pressed={form.avatar === opt}
                      >
                        <CubMascot avatar={opt} size={52} />
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="field">
              <label htmlFor="u-pwd">Starting password</label>
              <div className="row gap-sm">
                <input id="u-pwd" value={form.password} onChange={set('password')} required minLength={8} className="grow" />
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setForm((f) => ({ ...f, password: makePassword() }))}>
                  🎲 New one
                </button>
              </div>
              <div className="hint">Three friendly words plus digits — easy to type, hard to guess.</div>
            </div>

            <label className="checkline">
              <input type="checkbox" checked={form.mustChangePassword} onChange={set('mustChangePassword')} />
              Ask them to choose their own password on first sign-in
            </label>

            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setCreateOpen(false)}>Cancel</button>
              <button type="submit" className="btn" disabled={busy}>
                {busy ? 'Creating…' : 'Create account'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* ---------------- bulk modal ---------------- */}
      <Modal open={bulkOpen} title="Bulk add a class" onClose={() => setBulkOpen(false)} wide>
        {bulkResult ? (
          <div className="created-box">
            <h3>✅ Added {bulkResult.created.length} student{bulkResult.created.length === 1 ? '' : 's'}</h3>
            <p className="muted">
              Everyone starts with the password <strong>{bulk.defaultPassword}</strong> and will be
              asked to change it on first sign-in.
            </p>
            <div className="table-wrap" style={{ maxHeight: 260 }}>
              <table className="data-table">
                <thead><tr><th>User ID</th><th>Name</th></tr></thead>
                <tbody>
                  {bulkResult.created.map((c) => (
                    <tr key={c.id}><td><code className="code-chip">{c.userId}</code></td><td>{c.fullName}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            {bulkResult.skipped.length > 0 && (
              <Alert kind="warn">
                Skipped {bulkResult.skipped.length}: {bulkResult.skipped.map((s) => s.userId).join(', ')} —
                those User IDs were already taken.
              </Alert>
            )}
            <button type="button" className="btn mt-md" onClick={() => setBulkOpen(false)}>Done</button>
          </div>
        ) : (
          <form onSubmit={submitBulk}>
            {formError && <Alert kind="bad" onDismiss={() => setFormError('')}>{formError}</Alert>}

            <div className="form-row">
              <div className="field">
                <label htmlFor="b-school">School</label>
                <select id="b-school" value={bulk.schoolId} onChange={(e) => setBulk((b) => ({ ...b, schoolId: e.target.value }))} required>
                  <option value="">— choose a school —</option>
                  {schools.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="b-grade">Grade / class</label>
                <input id="b-grade" value={bulk.grade} onChange={(e) => setBulk((b) => ({ ...b, grade: e.target.value }))} placeholder="e.g. Grade 5" maxLength={20} />
              </div>
            </div>

            <div className="field">
              <label htmlFor="b-pwd">Password for everyone in this batch</label>
              <div className="row gap-sm">
                <input id="b-pwd" value={bulk.defaultPassword} onChange={(e) => setBulk((b) => ({ ...b, defaultPassword: e.target.value }))} required minLength={8} className="grow" />
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setBulk((b) => ({ ...b, defaultPassword: makePassword() }))}>
                  🎲 New one
                </button>
              </div>
            </div>

            <div className="field">
              <label htmlFor="b-list">Student list</label>
              <textarea
                id="b-list"
                value={bulk.text}
                onChange={(e) => setBulk((b) => ({ ...b, text: e.target.value }))}
                placeholder={'One per line:\nuserid, Full Name\n\naarav.g, Aarav Gupta\nmeera.s, Meera Shah'}
                required
                rows={9}
                style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: '0.95rem' }}
              />
              <div className="hint">
                {parseBulk(bulk.text).length} student{parseBulk(bulk.text).length === 1 ? '' : 's'} detected.
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setBulkOpen(false)}>Cancel</button>
              <button type="submit" className="btn" disabled={busy || !parseBulk(bulk.text).length}>
                {busy ? 'Creating…' : `Create ${parseBulk(bulk.text).length} accounts`}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* ---------------- assign school modal ---------------- */}
      <Modal
        open={Boolean(assignFor)}
        title={`Assign a school to ${assignFor?.fullName ?? ''}`}
        onClose={() => setAssignFor(null)}
      >
        <form onSubmit={doAssign}>
          <p className="muted">
            <strong>{assignFor?.userId}</strong> has no school, so no school admin can see them or
            report on their progress. Pick the school they belong to.
          </p>
          <div className="field">
            <label htmlFor="a-school">School</label>
            <select
              id="a-school"
              value={assignSchool}
              onChange={(e) => setAssignSchool(e.target.value)}
              required
            >
              <option value="">— choose a school —</option>
              {schools.map((sc) => (
                <option key={sc.id} value={sc.id}>{sc.name} ({sc.code})</option>
              ))}
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setAssignFor(null)}>Cancel</button>
            <button type="submit" className="btn" disabled={busy || !assignSchool}>
              {busy ? 'Saving…' : 'Assign school'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ---------------- reset password modal ---------------- */}
      <Modal open={Boolean(resetFor)} title={`Reset password for ${resetFor?.userId ?? ''}`} onClose={() => setResetFor(null)}>
        <form onSubmit={doReset}>
          <p className="muted">
            Give this new password to <strong>{resetFor?.fullName}</strong>. They will be asked to
            change it the next time they sign in.
          </p>
          <div className="field">
            <label htmlFor="r-pwd">New password</label>
            <div className="row gap-sm">
              <input id="r-pwd" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} required minLength={8} className="grow" />
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setResetPwd(makePassword())}>
                🎲 New one
              </button>
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setResetFor(null)}>Cancel</button>
            <button type="submit" className="btn" disabled={busy}>Set new password</button>
          </div>
        </form>
      </Modal>

      <Toast message={toast} onDone={() => setToast('')} />
    </>
  );
}
